import React, { useState, useEffect } from 'react';
import {
	Grid,
	Button,
	TextField,
	FormControl,
	InputLabel,
	Select,
	MenuItem,
	SelectChangeEvent,
} from '@mui/material';
import { IOnboardingSettings } from 'core/interfaces/onboardingInterface';
import { createOnboardingSettings } from 'core/API/onboarding';
import { getPartners } from 'core/API/partners';
import { Partner } from 'types';
import styles from '../SchedulerSetting.module.scss';

interface EditGeneralSettingsProps {
	settings: IOnboardingSettings | null;
	handleBackClick?: () => void;
	onSettingsUpdated?: () => void;
}

const EditGeneralSettings: React.FC<EditGeneralSettingsProps> = ({
	settings,
	handleBackClick,
	onSettingsUpdated,
}) => {
	const [settingsBuffer, setSettingsBuffer] = useState(() => {
		const getWorkplace = () => {
			if (settings?.workplace) {
				return settings.workplace;
			}
			const workplaceId = localStorage.getItem('workplace-id');
			return workplaceId ? `/api/workplaces/${workplaceId}` : '';
		};

		return {
			workplace: getWorkplace(),
			ein: settings?.ein || '',
			storageProvider: settings?.storageProvider || '',
			partnerId: '', // Store the selected partner ID
		};
	});

	const [partners, setPartners] = useState<Partner[]>([]);
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [isLoadingPartners, setIsLoadingPartners] = useState(true);
	const [error, setError] = useState<string | null>(null);

	useEffect(() => {
		loadPartners();
	}, []);

	const loadPartners = async () => {
		try {
			setIsLoadingPartners(true);
			const partnersData = await getPartners({ active: true });

			// Filter for Google Drive and OneDrive partners
			const storagePartners = partnersData.filter(
				(p) =>
					p.label?.toLowerCase().includes('google') ||
					p.label?.toLowerCase().includes('drive') ||
					p.label?.toLowerCase().includes('onedrive') ||
					p.label?.toLowerCase().includes('microsoft')
			);

			setPartners(storagePartners);

			// If only one partner exists, auto-select it
			if (storagePartners.length === 1 && !settingsBuffer.partnerId) {
				const partner = storagePartners[0];
				setSettingsBuffer((prev) => ({
					...prev,
					partnerId: partner['@id'] || `/api/partners/${partner.id}`,
				}));
			}
		} catch (err) {
			console.error('Error loading partners:', err);
		} finally {
			setIsLoadingPartners(false);
		}
	};

	const validateEIN = (ein: string): boolean => {
		// EIN format: XX-XXXXXXX (2 digits, hyphen, 7 digits)
		const einRegex = /^\d{2}-\d{7}$/;
		return einRegex.test(ein);
	};

	const validateSettings = (): boolean => {
		// Validate EIN
		if (!settingsBuffer.ein.trim()) {
			setError('EIN is required');
			return false;
		}

		if (!validateEIN(settingsBuffer.ein)) {
			setError('Invalid EIN format. Expected format: XX-XXXXXXX (e.g., 12-3456789)');
			return false;
		}

		// Validate partner selection
		if (!settingsBuffer.partnerId) {
			setError('Storage provider partner is required');
			return false;
		}

		if (partners.length === 0) {
			setError('No storage provider partner configured. Please configure partners first.');
			return false;
		}

		if (!settingsBuffer.workplace) {
			setError('Workplace is required');
			return false;
		}

		return true;
	};

	const handleSubmit = async (e: React.FormEvent) => {
		e.preventDefault();
		setError(null);

		if (!validateSettings()) {
			return;
		}

		try {
			setIsSubmitting(true);
			// Always use POST API as per requirement
			await createOnboardingSettings({
				ein: settingsBuffer.ein,
				storageProvider: settingsBuffer.partnerId as any,
				workplace: settingsBuffer.workplace,
			});

			onSettingsUpdated?.();
			setTimeout(() => {
				handleBackClick?.();
			}, 500);
		} catch (err: any) {
			setError(err?.message || 'Failed to update settings');
			setIsSubmitting(false);
		}
	};

	const handlePartnerChange = (event: SelectChangeEvent<string>) => {
		const partnerIri = event.target.value;
		setSettingsBuffer((prev) => ({
			...prev,
			partnerId: partnerIri,
		}));
	};

	return (
		<div className={styles.editGeneralSettings}>
			<Grid container direction="column" spacing={2} pl={2}>
				<Grid container direction="row" justifyContent="space-between">
					<Button className={styles.backButton} onClick={handleBackClick} variant="contained">
						Back
					</Button>
					<Button
						className={styles.submitButton}
						onClick={handleSubmit}
						disabled={isSubmitting}
						variant="contained"
					>
						{isSubmitting ? 'Saving...' : 'Save'}
					</Button>
				</Grid>
				<Grid>
					<h1 className={styles.formTitle}>Edit General Settings</h1>
				</Grid>
			</Grid>

			<form className={styles.formContainer} onSubmit={handleSubmit}>
				{error && <div style={{ color: '#d32f2f', marginBottom: 16 }}>{error}</div>}

				<TextField
					label="Employer Identification Number (EIN)"
					name="ein"
					value={settingsBuffer.ein}
					onChange={(e) => setSettingsBuffer({ ...settingsBuffer, ein: e.target.value })}
					placeholder="XX-XXXXXXX"
					className={styles.input}
					margin="normal"
					fullWidth
					required
					helperText="Format: XX-XXXXXXX (e.g., 12-3456789)"
					inputProps={{
						maxLength: 10,
					}}
				/>

				<FormControl fullWidth margin="normal" required className={styles.input}>
					<InputLabel>Storage Provider</InputLabel>
					<Select
						value={settingsBuffer.partnerId}
						onChange={handlePartnerChange}
						label="Storage Provider"
						disabled={isLoadingPartners || isSubmitting}
					>
						{partners.map((partner) => (
							<MenuItem
								key={partner.id}
								value={partner['@id'] || `/api/partners/${partner.id}`}
							>
								{partner.label}
							</MenuItem>
						))}
					</Select>
				</FormControl>

				{!isLoadingPartners && partners.length === 0 && (
					<div style={{ color: '#ed6c02', marginTop: 8, fontSize: '0.875rem' }}>
						No storage provider partner configured. Please configure in Partners section first.
					</div>
				)}
			</form>
		</div>
	);
};

export default EditGeneralSettings;
